/**
 * localStorage persistence.
 *
 * Everything is wrapped in try/catch: private-browsing modes and full quotas
 * both throw, and a habit tracker should never crash over that.
 */

import { normalizeRepeat } from './habits.js';
import { HABIT_KIND, SPORT_KIND, normalizeSportConfig } from './sport.js';

export const STORAGE_KEY = 'habits.ink/v1';

/**
 * The key used before the app was named habits.ink. Read once as a fallback so
 * anyone who used an earlier build keeps their habits; the next save writes to
 * STORAGE_KEY, after which this is never consulted again.
 */
export const LEGACY_STORAGE_KEY = 'paper-habits/v1';

export const EMPTY_STATE = { habits: [], completions: {}, sportLogs: {} };

/** Plain object check — arrays and null are not usable as maps. */
function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Coerce unknown parsed JSON into a valid state object.
 * Anything malformed falls back to an empty value rather than throwing.
 * @param {unknown} raw
 * @returns {{habits: object[], completions: Record<string, boolean>, sportLogs: Record<string, object>}}
 */
export function normalizeState(raw) {
  if (!raw || typeof raw !== 'object') return { habits: [], completions: {}, sportLogs: {} };

  const habits = Array.isArray(raw.habits)
    ? raw.habits
        .filter((h) => h && typeof h.id === 'string' && typeof h.startDate === 'string')
        .map((h) => {
          // A sport blueprint that cannot be salvaged demotes the entry back to
          // a plain habit rather than rendering a session with nothing in it.
          const sport = h.kind === SPORT_KIND ? normalizeSportConfig(h.sport) : null;
          return {
            id: h.id,
            name: typeof h.name === 'string' ? h.name : 'Untitled',
            kind: sport ? SPORT_KIND : HABIT_KIND,
            sport,
            repeat: normalizeRepeat(h.repeat),
            startDate: h.startDate,
            endDate: typeof h.endDate === 'string' ? h.endDate : null,
            exceptions: isPlainObject(h.exceptions) ? h.exceptions : {},
            createdAt: typeof h.createdAt === 'number' ? h.createdAt : 0,
          };
        })
    : [];

  const completions = isPlainObject(raw.completions) ? raw.completions : {};

  // sportLogs: 'habitId|baseDate' -> { fieldKey: stringValue }
  const sportLogs = isPlainObject(raw.sportLogs)
    ? Object.fromEntries(
        Object.entries(raw.sportLogs).filter(([, values]) => isPlainObject(values))
      )
    : {};

  return { habits, completions, sportLogs };
}

/**
 * Read state from a storage-like object.
 * @param {Storage} [store]
 * @returns {{habits: object[], completions: Record<string, boolean>}}
 */
export function loadState(store = safeStorage()) {
  if (!store) return normalizeState(null);
  try {
    // Fall back to the pre-rename key so an existing notebook survives the rename.
    const raw = store.getItem(STORAGE_KEY) ?? store.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return normalizeState(null);
    return normalizeState(JSON.parse(raw));
  } catch {
    return normalizeState(null);
  }
}

/**
 * Write state. Returns false when persistence was not possible.
 * @param {object} state
 * @param {Storage} [store]
 * @returns {boolean}
 */
export function saveState(state, store = safeStorage()) {
  if (!store) return false;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

/** localStorage if it exists and is reachable, otherwise null (SSR / tests). */
export function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
